import { useCallback, useRef, useState } from 'react';
import { CANDIDATE_VISIBILITY_ERROR_MESSAGES, saveCandidateVisibility } from '../../services/matchingApi.js';
import { isVisibleToCandidate, withCandidateVisible } from './candidateVisibility';

/**
 * 求職者に提案するか (推薦 PDF に載せるか。IN-M-011) の状態と保存。
 * 画面レイアウト_SCR-M-001.md §6.2「求職者に提案する求人の選択」、Issue #992。
 *
 * useAiMatching から呼ぶ (マッチ度の保存〈useScoreAdjustments〉と同じ分け方)。
 *
 * 保存中・失敗の文言は、どの推薦結果に対するものかを一緒に持つ。保存の間に表示する結果が
 * 替わったとき (保存中に実行した等)、前の結果の状態を新しい結果に出さないため (§6.2 手順 5)。
 * 保存は重ねて送らない。マッチ度の保存中も呼ばない (同じ推薦結果の行をロックして書くため。
 * §6.2「同時に保存するもの」)。
 *
 * 互いの保存中は ref で渡し合う (本フックはマッチ度の保存中を引数で受け取り、本フックの保存中を
 * candidateVisibilitySavingRef で返す)。ref は保存を始める時点で読む (描画の時点の値ではなく、
 * 押した時点の値で判定する。描画の時点の値では、片方の保存を始めてから再描画されるまでの間に
 * もう片方を押すと、両方が送られる)。
 *
 * @param {object|null} matchingResult 表示中の推薦結果
 * @param {Function} setMatchingResult
 * @param {Function} setOrderedItems 画面の並び (カードに渡す要素) の更新
 * @param {{current: boolean}} otherSavingRef ほかの保存中か (マッチ度の表示の設定・数字と、
 *   おすすめポイントの文〈Issue #777〉のどれでも。useAiMatching が savingRefs.js でまとめて渡す)
 */
function useCandidateVisibility(matchingResult, setMatchingResult, setOrderedItems, otherSavingRef) {
    const shownResultId = matchingResult?.id ?? null;
    // 保存の応答を受け取った時点で表示中の結果を知るために持つ (保存を始めた時点の値ではなく)
    const shownResultIdRef = useRef(shownResultId);
    shownResultIdRef.current = shownResultId;

    // 保存中の求人と、切り替えた後の値 ({ resultId, itemId, visible })。失敗の文言 ({ resultId, itemId, message })
    const [pending, setPending] = useState(null);
    const [failure, setFailure] = useState(null);
    // 保存中かを押した時点の値で判定するための印 (pending は描画の時点の値のため、再描画の前に 2 回押すと
    // 2 通送られうる。Step 2 レビュー [IMO]。マッチ度の保存中を ref で受け取るのと同じ考え方)
    const savingRef = useRef(false);

    const pendingForShown = pending && pending.resultId === shownResultId ? pending : null;
    const failureForShown = failure && failure.resultId === shownResultId ? failure : null;

    // 切り替えた時点で、その求人の設定だけを保存する (保存のボタンは設けない。§6.2 手順 1)。
    // カードの見え方と件数は、応答を受け取ってから切り替える (手順 2)
    const changeCandidateVisible = useCallback(
        async (jobPoolItemId, nextVisible) => {
            if (!matchingResult || savingRef.current || otherSavingRef?.current) return;
            const targetId = matchingResult.id;
            savingRef.current = true;
            setPending({ resultId: targetId, itemId: jobPoolItemId, visible: nextVisible });
            setFailure(null);

            // 保存中の印は finally で戻す (戻らないと、以後すべての保存が押せなくなり、エラーとしても見えない)
            try {
                let result;
                try {
                    result = await saveCandidateVisibility(targetId, jobPoolItemId, nextVisible);
                } catch (err) {
                    // 失敗は saveCandidateVisibility が結果で返す。ここに来るのは想定外の例外だけ。
                    // 保存中のまま止めない (切り替えも PDF 出力も押せなくなるため)
                    console.error('useCandidateVisibility: 求職者に提案するかの保存で例外が発生しました', err);
                    result = { ok: false, errorMessage: CANDIDATE_VISIBILITY_ERROR_MESSAGES.network };
                }

                if (result.ok) {
                    // 応答のその求人の値で入れ替える (画面が送った値をそのまま使わない)。
                    // ほかの求人の設定と並びは変えない (手順 3)。保存の間に別の結果へ替わっていたら入れない
                    const savedVisible = isVisibleToCandidate(result.recommendation);
                    const applySaved = (rec) =>
                        rec.job_pool_item_id === jobPoolItemId ? withCandidateVisible(rec, savedVisible) : rec;
                    setMatchingResult((prev) =>
                        prev && prev.id === targetId && Array.isArray(prev.recommendations)
                            ? { ...prev, recommendations: prev.recommendations.map(applySaved) }
                            : prev
                    );
                    if (shownResultIdRef.current === targetId) {
                        setOrderedItems((prev) => prev.map(applySaved));
                    }
                } else {
                    setFailure({ resultId: targetId, itemId: jobPoolItemId, message: result.errorMessage });
                }
            } finally {
                savingRef.current = false;
                setPending(null);
            }
        },
        [matchingResult, otherSavingRef, setMatchingResult, setOrderedItems]
    );

    return {
        // どの結果の保存でも真 (保存を重ねて送らない。マッチ度の操作と PDF 出力を押せなくするのにも使う)
        candidateVisibilitySaving: pending !== null,
        // 押した時点の保存中 (マッチ度のフックが、保存を始める時点で読む)
        candidateVisibilitySavingRef: savingRef,
        // 表示中の結果で保存中の求人と、切り替えた後の値 (そのカードのチェックの表示に使う)
        candidateVisibilityPending: pendingForShown
            ? { itemId: pendingForShown.itemId, visible: pendingForShown.visible }
            : null,
        candidateVisibilityError: failureForShown
            ? { itemId: failureForShown.itemId, message: failureForShown.message }
            : null,
        changeCandidateVisible,
    };
}

export default useCandidateVisibility;
